"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Users, Leaf, Compass, FileText, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const corePillars = [{
  id: "unite",
  title: "Tập hợp & đoàn kết hội viên",
  description: "Tập hợp các nhà khoa học, chuyên gia, tổ chức và cá nhân hoạt động trong lĩnh vực địa lý, tài nguyên, địa sinh thái và môi trường trên phạm vi toàn quốc.",
  icon: Users
}, {
  id: "protect-rights",
  title: "Bảo vệ quyền lợi hợp pháp",
  description: "Đại diện tiếng nói khoa học, bảo vệ quyền và lợi ích hợp pháp, chính đáng của hội viên trong các hoạt động nghiên cứu, ứng dụng và tư vấn phản biện xã hội.",
  icon: ShieldCheck
}, {
  id: "sustainability",
  title: "Bảo vệ môi trường & phát triển bền vững",
  description: "Đóng góp luận cứ khoa học địa lý cho công tác quản lý tài nguyên, quy hoạch không gian sinh thái, thích ứng biến đổi khí hậu và phát triển bền vững đất nước.",
  icon: Leaf
}];
const legalPoints = [{
  id: "nature",
  label: "Tính chất tổ chức",
  value: "Tổ chức xã hội - nghề nghiệp tự nguyện, hoạt động theo Hiến pháp và pháp luật Việt Nam."
}, {
  id: "mandate",
  label: "Quyết định thành lập",
  value: "Quyết định số 483/QĐ-BNV ngày 02/4/2021 của Bộ trưởng Bộ Nội vụ."
}, {
  id: "charter",
  label: "Điều lệ phê duyệt",
  value: "Quyết định số 1157/QĐ-BNV ngày 03/11/2021 của Bộ trưởng Bộ Nội vụ."
}, {
  id: "scope",
  label: "Phạm vi hoạt động",
  value: "Toàn quốc, chịu sự quản lý nhà nước của Bộ Nội vụ và các Bộ, ngành liên quan."
}];
export default function Tenets() {
  const shouldReduceMotion = useReducedMotion();
  const transitionSpec = shouldReduceMotion ? {
    duration: 0.1
  } : {
    duration: 0.38,
    ease: [0.16, 1, 0.3, 1]
  };
  const initialMotion = shouldReduceMotion ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 16
  };
  return <section id="tôn-chỉ-mục-đích" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={initialMotion} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={transitionSpec} className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-accent text-primary border border-border text-xs font-semibold uppercase tracking-wider">
            <Compass className="size-3.5 text-primary" />
            <span>Văn kiện tổ chức</span>
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-secondary font-default mb-4">
            Tôn chỉ mục đích
          </h1>

          {/* Double-rule institutional accent */}
          <div className="relative w-full max-w-xs h-2.5 mb-6 flex flex-col justify-between">
            <div className="h-[3px] w-16 bg-secondary rounded-xs" />
            <Separator className="bg-border" />
          </div>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hội Địa lý môi trường Việt Nam là tổ chức xã hội - nghề nghiệp của các tổ chức, công dân Việt Nam hoạt động trong lĩnh vực nghiên cứu, giảng dạy, ứng dụng và quản lý về địa lý môi trường, tự nguyện thành lập nhằm bảo vệ quyền và lợi ích hợp pháp của hội viên, phục vụ sự nghiệp bảo vệ môi trường và phát triển bền vững đất nước.
          </p>
        </motion.div>

        {/* 3 Core Tenet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {corePillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={index} initial={initialMotion} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            ...transitionSpec,
            delay: shouldReduceMotion ? 0 : index * 0.08
          }} className="rounded-sm border border-border bg-card text-card-foreground p-6 sm:p-8 flex flex-col justify-between shadow-xs hover:border-primary transition-[border-color,box-shadow] duration-200 relative overflow-hidden">
                {/* Structural top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary" />

                <div>
                  <div className="size-11 rounded-sm bg-accent flex items-center justify-center mb-6 text-primary border border-border">
                    <Icon className="size-5" />
                  </div>

                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3 font-default">
                    {pillar.title}
                  </h3>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">Mục 0{index + 1}</span>
                  <span className="text-primary font-medium">Điều lệ Hội</span>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Legal Mandate & Framework Summary Card */}
        <motion.div initial={initialMotion} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={transitionSpec} className="rounded-sm border border-border bg-muted/40 p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                <FileText className="size-4" />
                <span>Cơ sở pháp lý nền tảng</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default mb-3">
                Hoạt động theo khuôn khổ pháp luật nhà nước
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                Mọi chủ trương, phương hướng hoạt động và tư vấn chuyên môn của Hội đều tuân thủ các quy định hiện hành, phối hợp chặt chẽ với cơ quan quản lý nhà nước về tài nguyên, môi trường và khoa học công nghệ.
              </p>

              <div className="flex flex-wrap gap-3">
                <Button asChild variant="default" size="default" className="rounded-sm font-semibold">
                  <Link to="/OrganizationStructurePage">
                    Cơ cấu tổ chức
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="default" className="rounded-sm font-semibold">
                  <Link to="/BusinessAreasPage">
                    Lĩnh vực hoạt động
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {legalPoints.map((item, idx) => <div key={item.id} data-index={idx} className="rounded-sm border border-border bg-card p-4 sm:p-5 flex flex-col justify-start">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 font-mono">
                    {item.label}
                  </span>
                  <span className="text-sm font-medium text-foreground leading-snug">
                    {item.value}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}